import React, {
  ElementType,
} from 'react';
import {
  useLocation,
  useNavigate,
} from 'react-router-dom';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import NotesIcon from 'core/icons/notes';
import InsuranceIcon from 'core/icons/insurance';
import ConsentFormIcon from 'core/icons/consentForm';
import PeopleIcon from '@mui/icons-material/People';
import TagIcon from '@mui/icons-material/LocalOffer';
import MapsHomeWorkOutlinedIcon from '@mui/icons-material/MapsHomeWorkOutlined';
import CalendarIcon from 'core/icons/calendar';
import SETTINGS_PATHS from 'views/EMR/Settings/paths';
import useEMRAccess from 'views/EMR/hooks/useEMRAccess';
import {
  usePCCAvailability,
} from 'views/EMR/hooks/pcc';
import PCCIcon from 'core/icons/pcc';

const iconClassName = '!w-4 !h-4';
const tabClassName = '!p-2 !min-h-[48px] !normal-case !font-semibold';

type SettingsTab = {
  Icon: ElementType<{
    className?: string;
  }>;
  value: string;
  label: string;
  requiresPCCActivation?: boolean;
};

const tabs: SettingsTab[] = [{
  Icon: CalendarIcon,
  value: SETTINGS_PATHS.APPOINTMENTS,
  label: 'Appointments',
}, {
  Icon: ConsentFormIcon,
  value: SETTINGS_PATHS.FORMS,
  label: 'Forms',
}, {
  Icon: MapsHomeWorkOutlinedIcon,
  value: SETTINGS_PATHS.ORGANIZATION,
  label: 'Organization',
}, {
  Icon: InsuranceIcon,
  value: SETTINGS_PATHS.PAYERS,
  label: 'Payers',
}, {
  Icon: TagIcon,
  value: SETTINGS_PATHS.TAGS,
  label: 'Tags',
}, {
  Icon: PeopleIcon,
  value: SETTINGS_PATHS.USERS,
  label: 'Users',
}, {
  Icon: NotesIcon,
  value: SETTINGS_PATHS.VISIT_NOTES,
  label: 'Visit Notes',
}, {
  Icon: PCCIcon,
  value: SETTINGS_PATHS.PCC,
  label: 'PCC',
  requiresPCCActivation: true,
}];

export default function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    canPath,
    defaultSettingsPath,
    isPCCActionAvailable,
  } = useEMRAccess();
  const {
    isLoading: isLoadingPCCAvailability,
    isPCCAvailable,
  } = usePCCAvailability();
  const isPCCTabAvailable = isPCCActionAvailable
    && isPCCAvailable
    && !isLoadingPCCAvailability;
  const settingsTabs = tabs.filter(({
    requiresPCCActivation = false,
    value,
  }) => {
    const hasActivationAccess = !requiresPCCActivation || isPCCTabAvailable;

    return canPath(value) && hasActivationAccess;
  });
  const defaultTabValue = settingsTabs[0]?.value ?? defaultSettingsPath;
  const activeTab = settingsTabs.find(({ value }) => location.pathname.startsWith(value))?.value
    ?? defaultTabValue;

  return (
    <Tabs
      className="!min-h-[48px] w-full border-b border-b-gray-200 px-4 populate-route-tabs"
      value={activeTab}
      onChange={(event, value: string) => { navigate(value); }}
    >
      {settingsTabs.map(({ Icon, label, value }) => (
        <Tab
          key={value}
          className={tabClassName}
          iconPosition="start"
          icon={<Icon className={iconClassName} />}
          label={label}
          value={value}
        />
      ))}
    </Tabs>
  );
}
